import posthog from 'posthog-js'

import config from 'lib/config'
import type { EntryTrackingProperties } from 'lib/journeySink'

import {
  PROFILE_FLAG_STORAGE_KEY,
  PROFILE_FLAGS,
  ProfileFlag,
} from './constants'

// Mirrors the de-duplication cache when localStorage is unavailable (private
// mode). Worst case a flag is re-sent — PostHog treats it as idempotent.
let memorySentFlags: ProfileFlag[] | null = null

const readSentFlags = (): ProfileFlag[] => {
  try {
    const raw = window?.localStorage?.getItem(PROFILE_FLAG_STORAGE_KEY)
    if (!raw) {
      return memorySentFlags ?? []
    }
    return JSON.parse(raw)
  } catch {
    return memorySentFlags ?? []
  }
}

const writeSentFlags = (flags: ProfileFlag[]): void => {
  memorySentFlags = flags
  try {
    window?.localStorage?.setItem(
      PROFILE_FLAG_STORAGE_KEY,
      JSON.stringify(flags)
    )
  } catch {
    // Private mode / quota. memorySentFlags already holds the update.
  }
}

// No identity check here, deliberately. posthog.setPersonProperties() runs the
// SDK's own person-processing gate first, which WRITES the sticky $epp flag and
// only refuses when person_profiles is 'never' — so a write from an anonymous
// visitor enables the profile rather than being dropped. The previous
// _isIdentified() guard refused writes PostHog would have accepted, which is why
// the pre-account journey showed as a bare UUID with no shop, name or number.
//
// Do NOT reach for posthog._hasPersonProcessing() here: it is declared in the
// SDK's .d.ts but minified away in the shipped bundle, so it reads as undefined
// at runtime and silently disables every write.
const canWriteProfile = (): boolean => Boolean(config.POSTHOG_API_KEY)

// Opts this browser into person processing for every later event via the SDK's
// sticky $epp flag. Self-deduplicating, so it is safe to call on every event.
// Without it the session's first $groupidentify is dropped server-side as
// `invalid_event_when_process_person_profile_is_false` (41 recorded).
export const ensurePersonProfile = (): void => {
  if (!config.POSTHOG_API_KEY) {
    return
  }
  posthog?.createPersonProfile?.()
}

// Raises a lifecycle flag to true, at most once per agent per browser.
export const setProfileFlag = (flag: ProfileFlag): void => {
  const sent = readSentFlags()
  if (sent.includes(flag)) {
    return
  }
  if (!canWriteProfile()) {
    return
  }
  posthog?.setPersonProperties?.({ [flag]: true })
  writeSentFlags([...sent, flag])
}

// Seeds every flag as false the first time we see this person, so the property
// always exists and "never visited the demo store" is a filterable value rather
// than an absence. $set_once means a flag already raised is left untouched.
export const initProfileFlags = (): void => {
  if (!canWriteProfile()) {
    return
  }
  const seeded = PROFILE_FLAGS.reduce(
    (acc, flag) => ({ ...acc, [flag]: false }),
    {}
  )
  posthog?.setPersonProperties?.({}, seeded)
}

interface ContactIdentity {
  business_name?: string
  business_phone?: string
  website?: string
  country?: string
  support_email?: string
}

// An absent field must not be written as an empty string — that would later read
// as "we asked and they had none", a different fact from "we never knew".
const withoutBlanks = (props: object): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(props).filter(([, value]) => value || value === false)
  )

// Stamps how this merchant arrived, the moment the login/signup page is seen.
// $set_once: someone returning through a different door keeps the door that
// actually explains their signup.
export const setEntryIdentity = (entry: EntryTrackingProperties): void => {
  ensurePersonProfile()
  if (!canWriteProfile()) {
    return
  }
  posthog?.setPersonProperties?.(
    {},
    withoutBlanks({
      signup_source: entry?.via_shopify ? 'shopify_install' : 'direct',
      first_shop: entry?.shop,
      first_entry_kind: entry?.entry_kind,
      first_is_icp: entry?.is_icp,
    })
  )
}

// Stamps the business details typed during onboarding, so a merchant who abandons
// before creating an account is a nameable lead rather than a UUID. $set, not
// $set_once: a corrected number should win.
export const setContactIdentity = (contact: ContactIdentity): void => {
  ensurePersonProfile()
  const props = withoutBlanks(contact)
  if (!canWriteProfile() || !Object.keys(props).length) {
    return
  }
  posthog?.setPersonProperties?.(props)
}

// Called on logout: the next agent signing in on this browser must send their
// own flags rather than inheriting the previous agent's cache.
export const clearProfileFlags = (): void => {
  memorySentFlags = null
  try {
    window?.localStorage?.removeItem(PROFILE_FLAG_STORAGE_KEY)
  } catch {
    // Private mode. memorySentFlags is already cleared.
  }
}
